export type SelectedAwareTagAction = 'add' | 'remove' | 'override' | 'clear';

const getDistinctKeys = <T>(values: T[], getKey: (value: T) => string | number | null | undefined) =>
  Array.from(
    new Set(
      values
        .map(getKey)
        .filter((value): value is string | number => value !== null && value !== undefined && value !== '')
    )
  );

export const getSelectedAwareTagOptions = <TOption, TItem>({
  tagOptions,
  selectedItems,
  tagAction,
  getOptionKey,
  getSelectedItemTagKeys,
}: {
  tagOptions: TOption[];
  selectedItems: TItem[];
  tagAction: SelectedAwareTagAction;
  getOptionKey: (option: TOption) => string | number | null | undefined;
  getSelectedItemTagKeys: (item: TItem) => Array<string | number | null | undefined>;
}) => {
  if (!selectedItems.length || (tagAction !== 'add' && tagAction !== 'remove')) {
    return tagOptions;
  }

  const itemTagKeys = selectedItems.map((item) => new Set(getDistinctKeys(getSelectedItemTagKeys(item), (key) => key)));

  if (tagAction === 'add') {
    return tagOptions.filter((option) => {
      const optionKey = getOptionKey(option);
      return optionKey == null || !itemTagKeys.every((keys) => keys.has(optionKey));
    });
  }

  return tagOptions.filter((option) => {
    const optionKey = getOptionKey(option);
    return optionKey != null && itemTagKeys.some((keys) => keys.has(optionKey));
  });
};

export const getValidSelectedAwareTags = <TOption, TItem>({
  tags,
  tagOptions,
  selectedItems,
  tagAction,
  getOptionKey,
  getSelectedItemTagKeys,
}: {
  tags: TOption[];
  tagOptions: TOption[];
  selectedItems: TItem[];
  tagAction: SelectedAwareTagAction;
  getOptionKey: (option: TOption) => string | number | null | undefined;
  getSelectedItemTagKeys: (item: TItem) => Array<string | number | null | undefined>;
}) => {
  const validOptionKeys = new Set(
    getSelectedAwareTagOptions({
      tagOptions,
      selectedItems,
      tagAction,
      getOptionKey,
      getSelectedItemTagKeys,
    }).map(getOptionKey)
  );

  return tags.filter((tag) => {
    const tagKey = getOptionKey(tag);
    return tagKey == null || validOptionKeys.has(tagKey);
  });
};
